import { useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Link } from "@mzc-pdc/ui";
import { IconCheckLarge, IconCopy, IconOpenInNew } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useCopyToClipboard from "@hooks/useCopyToClipboard";
import { getFaviconUrl, getLinkLabel } from "@utils/parse-text-links";

const FAVICON_SIZE = 16;

/**
 * 주소 하나를 "파비콘 + 축약한 주소"로 보여 주는 링크. 클릭하면 새 창으로 열고,
 * 옆의 복사 버튼으로 축약 전 원문 주소를 클립보드에 담는다. (DAM-858)
 *
 * 파비콘은 해당 도메인의 /favicon.ico 를 직접 부르므로 없는 사이트에서는 로드가 실패한다.
 * 엑박이 남지 않게 실패하면 링크 아이콘으로 대체한다(헤더 로고와 같은 방식).
 *
 * 복사 버튼은 링크(a) 바깥에 둔다 — a 안에 button 을 넣으면 안 되고, 복사하려다 새 창이
 * 열려서도 안 되기 때문이다.
 *
 * @param {Object} props
 * @param {string} props.href - 열어 줄 주소
 * @param {number} [props.maxLength] - 라벨 축약 기준 길이. 생략하면 유틸 기본값.
 */
const FaviconLink = ({ href, maxLength }) => {
    const { t } = useTranslation();
    const { copy, isCopied } = useCopyToClipboard();
    const faviconUrl = getFaviconUrl(href);
    const [failedSrc, setFailedSrc] = useState(null);
    const hasFailed = !!faviconUrl && failedSrc === faviconUrl;

    return (
        <Box
            component={"span"}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                maxWidth: "100%",
                verticalAlign: "text-bottom",
            }}
        >
            <Tooltip title={href} maxWidth={360}>
                <Link
                    href={href}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                    underline={"none"}
                    // 파비콘이 뜨면 새 창 아이콘이 가려지므로 접근성 이름으로 알린다 (WCAG 3.2.5)
                    aria-label={`${getLinkLabel(href, maxLength)} (${t(
                        `common::label::Open in new window`,
                        `Open in new window`,
                    )})`}
                    // 행 전체에 클릭이 걸린 화면에서도 링크만 열리게 한다.
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        maxWidth: "100%",
                        wordBreak: "break-all",
                        // rgb(60, 61, 79) — 라이트 팔레트의 greySecondary[800](= text.primary)
                        color: theme.palette.greySecondary[800],
                    }}
                >
                    {faviconUrl && !hasFailed ? (
                        <Box
                            component={"img"}
                            src={faviconUrl}
                            alt={""}
                            width={FAVICON_SIZE}
                            height={FAVICON_SIZE}
                            onError={() => setFailedSrc(faviconUrl)}
                            // 파비콘은 값에 적힌 임의 도메인으로 나가는 요청이다.
                            // 어느 화면에서 열람했는지가 새어 나가지 않게 referrer 를 끊고,
                            // 화면 밖 링크는 실제로 보일 때까지 요청하지 않는다.
                            referrerPolicy={"no-referrer"}
                            loading={"lazy"}
                            sx={{ flexShrink: 0, objectFit: "contain" }}
                        />
                    ) : (
                        <IconOpenInNew size={FAVICON_SIZE} htmlColor={"inherit"} />
                    )}
                    {/* 밑줄은 라벨에만 건다 — 링크 전체에 걸면 파비콘 아래까지 그어진다 */}
                    <Box component={"span"} sx={{ textDecoration: "underline" }}>
                        {getLinkLabel(href, maxLength)}
                    </Box>
                </Link>
            </Tooltip>
            <Tooltip
                title={isCopied(href) ? t(`common::label::Copied`, `Copied`) : t(`common::label::Copy`, `Copy`)}
            >
                <IconButton
                    circled
                    size={"small"}
                    variant={"text"}
                    color={"inherit"}
                    aria-label={t(`common::label::Copy`, `Copy`)}
                    onClick={(e) => {
                        // 복사 버튼이 링크 이동이나 바깥 행 클릭을 함께 일으키지 않게 한다.
                        e.preventDefault();
                        e.stopPropagation();
                        copy(href);
                    }}
                    sx={{ flexShrink: 0 }}
                >
                    {isCopied(href) ? <IconCheckLarge size={16} color={"success"} /> : <IconCopy size={16} />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

FaviconLink.propTypes = {
    href: PropTypes.string.isRequired,
    maxLength: PropTypes.number,
};

export default FaviconLink;
